import { motion } from "framer-motion";
import { BedDouble, Users, Maximize, Wifi, Snowflake, Check, Star } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { imgUrl } from "@/lib/api";

const RoomCard = ({ room, index, onBook }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    className="group flex flex-col rounded-2xl overflow-hidden bg-card border border-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    data-testid={`room-card-${room.room_id}`}
  >
    <div className="relative h-56 overflow-hidden">
      <img src={imgUrl(room.image)} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      {room.images?.length > 1 && <span className="absolute top-3 right-3 text-[11px] bg-black/50 text-white rounded-full px-2 py-0.5" data-testid={`room-photo-count-${room.room_id}`}>{room.images.length} photos</span>}
      <div className="absolute top-3 left-3 flex gap-2">
        {room.ac && <Badge className="bg-secondary text-secondary-foreground border-0"><Snowflake className="h-3 w-3 mr-1" />AC</Badge>}
        {!room.is_available && <Badge variant="destructive">Sold Out</Badge>}
      </div>
      <div className="absolute bottom-3 right-3 rounded-lg bg-background/90 backdrop-blur px-3 py-1.5 text-right">
        {room.original_price > room.price_per_night && (
          <span className="text-xs text-muted-foreground line-through font-mono mr-1">₹{room.original_price}</span>
        )}
        <span className="font-mono font-bold text-primary text-lg">₹{room.price_per_night}</span>
        <span className="text-[11px] text-muted-foreground">/night</span>
      </div>
    </div>

    <div className="flex flex-col flex-1 p-5 lg:p-6">
      <h3 className="font-serif text-xl font-semibold">{room.name}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed line-clamp-2">{room.description}</p>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <Spec icon={<Users className="h-4 w-4" />} text={room.capacity} />
        <Spec icon={<BedDouble className="h-4 w-4" />} text={room.bed} />
        <Spec icon={<Maximize className="h-4 w-4" />} text={room.size} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {room.amenities.slice(0, 4).map((a) => (
          <span key={a} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted rounded-full px-2.5 py-1">
            <Check className="h-3 w-3 text-secondary" />{a}
          </span>
        ))}
      </div>

      <Button
        onClick={() => onBook(room)}
        disabled={!room.is_available}
        data-testid={`book-now-button-${room.room_id}`}
        className="mt-5 w-full rounded-xl bg-primary hover:bg-primary/90 h-11"
      >
        {room.is_available ? "Book Now" : "Unavailable"}
      </Button>
    </div>
  </motion.div>
);

const Spec = ({ icon, text }) => (
  <div className="flex flex-col items-center gap-1 rounded-lg bg-muted/60 py-2 px-1 text-center">
    <span className="text-primary">{icon}</span>
    <span className="text-muted-foreground leading-tight">{text}</span>
  </div>
);

export const Rooms = ({ rooms, loading, onBook }) => (
  <section id="rooms" className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
    <div className="max-w-2xl">
      <span className="text-sm font-semibold uppercase tracking-[0.15em] text-primary">Stay With Us</span>
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold">Our Room Offerings</h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">
        Meticulously maintained AC rooms with daily housekeeping, high-speed Wi-Fi and plush bedding. Best rates guaranteed — book direct.
      </p>
    </div>

    {loading ? (
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-96 rounded-2xl bg-muted animate-pulse" />)}
      </div>
    ) : (
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {rooms.map((r, i) => <RoomCard key={r.room_id} room={r} index={i} onBook={onBook} />)}
      </div>
    )}

    <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
      Free cancellation · Pay at hotel · No booking fees
    </div>
  </section>
);
